// Shared cache of the bulk match sample (25,000 matches per mode since
// MINIMUM_VERSION) used by the analytics screens.
//
// Contract (other modules rely on this exactly):
//   - `state` is a Vue reactive object with keys `ranked` and `normal`, each
//     `{ loading, loaded, error, revision }`; pick with
//     `state[isRanked ? 'ranked' : 'normal']`. Loading/error UI reads it directly.
//   - `ensureMatches(modes = [true, false])` starts every requested mode that is
//     not already loaded or in flight, concurrently, and resolves with
//     `Promise.allSettled`-style results in the order of `modes`. Loaded modes
//     are never refetched; in-flight requests are shared.
//   - `retryMatches(modes)` explicitly retries failed modes.
//   - `fetchAllMatches()` is an alias of `ensureMatches()`.
//   - `onChange(cb)` calls `cb(isRanked)` after each successful publication
//     only; callback failures are isolated.
//   - `isFetching` (ref) is true while any mode loads; `latestMatchAt` (ref) is
//     the newest match Date across published modes.
//   - Arrays and records handed out are `markRaw` and owned by the cache: treat
//     them as read-only. Query results are reused until the mode republishes.
import { markRaw, reactive, ref } from 'vue'
import utils from '@/utils'
import api_communication from '@/api_communication'
import { MINIMUM_VERSION } from '@/constants/Version'
import { normalizeMatch } from '@/service/matchnormalization'
import { ensureMetadata as defaultEnsureMetadata } from '@/service/metadata'
import { mark, now, processInChunks, record } from '@/service/performance'

export const BULK_SAMPLE_SIZE = 25000

export const modeKey = (isRanked) => (isRanked ? 'ranked' : 'normal')

const MODE_KEYS = ['ranked', 'normal']

const UNKNOWN_VERSION = 'unknown'

const createModeState = () => ({ loading: false, loaded: false, error: null, revision: 0 })

const uniqueModes = (modes) => {
  const list = Array.isArray(modes) ? modes : [modes]
  const seen = new Set()
  const out = []
  for (const mode of list) {
    const isRanked = Boolean(mode)
    if (!seen.has(isRanked)) {
      seen.add(isRanked)
      out.push(isRanked)
    }
  }
  return out
}

const DAY_MS = 86400000

/**
 * @param {object} [deps] test seams; production uses the defaults
 * @param {(isRanked: boolean) => Promise<{ matches: object[], bytes?: number, networkMs?: number, decodeMs?: number }>} [deps.fetchMatches]
 * @param {() => Promise<{ ok: boolean }>} [deps.ensureMetadata]
 * @param {{ getHeroName: Function, getItemName: Function }} [deps.normalizationHelpers]
 * @param {Function} [deps.processInChunks]
 * @param {object} [deps.chunkOptions] budget/maxChunk passed to processInChunks
 * @param {() => number} [deps.now] wall clock for fetchTimestamps (Date.now)
 */
export function createMatchesCache(deps = {}) {
  const fetchMatches =
    deps.fetchMatches ||
    ((isRanked) => api_communication.getMatchesDetailed(BULK_SAMPLE_SIZE, null, MINIMUM_VERSION, isRanked))
  const ensureMetadata = deps.ensureMetadata || defaultEnsureMetadata
  const normalizationHelpers = deps.normalizationHelpers || {
    getHeroName: utils.getHeroNameById,
    getItemName: utils.getItemNameById,
  }
  const chunker = deps.processInChunks || processInChunks
  const chunkOptions = deps.chunkOptions
  const wallClock = deps.now || Date.now

  const state = reactive({
    ranked: createModeState(),
    normal: createModeState(),
  })

  const isFetching = ref(false)
  const latestMatchAt = ref(null)

  // `${versionBucket}_${isRanked}` -> markRaw array. Plain object on purpose:
  // consumers check `Object.keys(cache).length` and this must stay cheap and
  // non-reactive.
  const cache = {}
  const fetchTimestamps = {}

  const inflight = { ranked: null, normal: null }
  const latestByMode = { ranked: null, normal: null }
  const timings = { ranked: null, normal: null }

  // Per-mode memo of getMatches() results, keyed by `${version}|${exclusive}`.
  // Cleared when the mode republishes. Callers get the same array back for the
  // same query, so a component re-rendering with the same filters does no work.
  const queryCache = { ranked: new Map(), normal: new Map() }
  let versionOptionsCache = null
  const dailyCountsCache = { ranked: null, normal: null }

  let listeners = []

  const updateFetching = () => {
    isFetching.value = state.ranked.loading || state.normal.loading
  }

  const updateLatest = () => {
    let latest = null
    for (const key of MODE_KEYS) {
      const candidate = latestByMode[key]
      if (candidate && (!latest || candidate > latest)) latest = candidate
    }
    latestMatchAt.value = latest
  }

  const emitChange = (isRanked) => {
    // Copy so a callback that unsubscribes itself does not skip a neighbour.
    for (const cb of listeners.slice()) {
      try {
        cb(isRanked)
      } catch (err) {
        console.error('MatchesCache listener failed:', err)
      }
    }
  }

  const indexByVersion = (matches, isRanked) => {
    const grouped = {}
    const bucketByVersion = new Map()
    let latestMs = -Infinity

    for (let i = 0; i < matches.length; i++) {
      const match = matches[i]
      markRaw(match)

      const playedAtMs = match.playedAtMs
      if (typeof playedAtMs === 'number' && !Number.isNaN(playedAtMs) && playedAtMs > latestMs) {
        latestMs = playedAtMs
      }

      let bucket = UNKNOWN_VERSION
      if (match.version) {
        bucket = bucketByVersion.get(match.version)
        if (bucket === undefined) {
          bucket = utils.getVersionBucket(match.version)
          bucketByVersion.set(match.version, bucket)
        }
      }

      const key = `${bucket}_${isRanked}`
      let list = grouped[key]
      if (!list) {
        list = []
        grouped[key] = list
      }
      list.push(match)
    }

    return { grouped, latest: latestMs === -Infinity ? null : new Date(latestMs) }
  }

  const publish = (isRanked, grouped, latest) => {
    const key = modeKey(isRanked)
    const suffix = `_${isRanked}`
    const stamp = wallClock()

    // Replace this mode's buckets atomically with respect to consumers: every
    // synchronous read after this block sees only the new data.
    for (const cacheKey of Object.keys(cache)) {
      if (cacheKey.endsWith(suffix)) {
        delete cache[cacheKey]
        delete fetchTimestamps[cacheKey]
      }
    }
    for (const cacheKey in grouped) {
      cache[cacheKey] = markRaw(grouped[cacheKey])
      fetchTimestamps[cacheKey] = stamp
    }

    queryCache[key].clear()
    dailyCountsCache[key] = null
    versionOptionsCache = null
    latestByMode[key] = latest
    updateLatest()

    const modeState = state[key]
    modeState.revision++
    modeState.loaded = true
    modeState.error = null

    emitChange(isRanked)
  }

  const loadMode = async (isRanked) => {
    const key = modeKey(isRanked)
    const modeState = state[key]
    modeState.loading = true
    modeState.error = null
    updateFetching()

    const totalStart = now()
    mark(`matches:${key}:start`)

    try {
      // Metadata and the match request run concurrently; normalization needs
      // both. Without names ready, records would bake in "Unknown".
      const [metadataStatus, response] = await Promise.all([ensureMetadata(), fetchMatches(isRanked)])
      if (!metadataStatus || !metadataStatus.ok) {
        throw new Error('Hero/item metadata is unavailable, cannot normalize matches')
      }

      const matches = Array.isArray(response && response.matches) ? response.matches : []

      const normalizeStart = now()
      const chunkStats = await chunker(matches, (match) => normalizeMatch(match, normalizationHelpers), chunkOptions)
      record('matches:normalize', normalizeStart, now(), { mode: key, count: matches.length, chunks: chunkStats.chunks })

      const indexStart = now()
      const { grouped, latest } = indexByVersion(matches, isRanked)
      const indexMs = now() - indexStart
      record('matches:index', indexStart, now(), { mode: key, count: matches.length, buckets: Object.keys(grouped).length })

      publish(isRanked, grouped, latest)

      const totalMs = now() - totalStart
      timings[key] = {
        count: matches.length,
        bytes: response.bytes ?? null,
        networkMs: response.networkMs ?? null,
        decodeMs: response.decodeMs ?? null,
        normalizeMs: chunkStats.duration,
        normalizeChunks: chunkStats.chunks,
        indexMs,
        totalMs,
      }
      record('matches:total', totalStart, now(), { mode: key, ...timings[key] })
      console.info(
        `MatchesCache ${key}: ${matches.length} matches in ${totalMs.toFixed(0)}ms ` +
          `(network ${fmt(response.networkMs)}, decode ${fmt(response.decodeMs)}, ` +
          `normalize ${fmt(chunkStats.duration)} in ${chunkStats.chunks} chunks, index ${fmt(timings[key].indexMs)})`
      )

      return { isRanked, count: matches.length, revision: modeState.revision }
    } catch (err) {
      console.error(`Error fetching all matches (isRanked=${isRanked}):`, err)
      modeState.error = err
      throw err
    } finally {
      modeState.loading = false
      inflight[key] = null
      updateFetching()
    }
  }

  const requestMode = (isRanked) => {
    const key = modeKey(isRanked)
    const modeState = state[key]

    if (modeState.loaded) {
      return Promise.resolve({ isRanked, count: countMode(isRanked), revision: modeState.revision, cached: true })
    }
    if (inflight[key]) {
      return inflight[key]
    }

    const promise = loadMode(isRanked)
    inflight[key] = promise
    // A rejection is surfaced through the settled results of every caller;
    // this no-op handler only stops it from also being reported as unhandled
    // when the promise sits in `inflight` without a waiting caller.
    promise.catch(() => {})
    return promise
  }

  const countMode = (isRanked) => {
    const suffix = `_${isRanked}`
    let count = 0
    for (const cacheKey in cache) {
      if (cacheKey.endsWith(suffix)) count += cache[cacheKey].length
    }
    return count
  }

  /**
   * Ensures the given modes are loaded. Failed modes are requested again
   * (nothing is loaded for them), in-flight ones are shared, loaded ones are
   * returned immediately. Resolves with allSettled-style results.
   */
  const ensureMatches = (modes = [true, false]) => {
    const list = uniqueModes(modes)
    return Promise.allSettled(list.map(requestMode))
  }

  /**
   * Explicit retry for modes in an error state. Modes that are loaded or
   * loading are left alone; their current result is returned.
   */
  const retryMatches = (modes = [true, false]) => {
    const list = uniqueModes(modes)
    for (const isRanked of list) {
      const modeState = state[modeKey(isRanked)]
      if (modeState.error && !modeState.loading) modeState.error = null
    }
    return Promise.allSettled(list.map(requestMode))
  }

  const fetchAllMatches = (modes) => ensureMatches(modes)

  const isLoaded = (isRanked) => state[modeKey(isRanked)].loaded

  const getModeState = (isRanked) => state[modeKey(isRanked)]

  /**
   * Matches of one mode for a version filter. Same semantics as before:
   * exclusive matches the version bucket exactly, otherwise every bucket
   * greater or equal. The returned array is cached per query and shared;
   * do not mutate it.
   */
  const getMatches = (version, isRanked = true, exclusiveVersion = false) => {
    const key = modeKey(isRanked)
    const queryKey = `${version}|${exclusiveVersion ? 1 : 0}`
    const memo = queryCache[key]
    const cached = memo.get(queryKey)
    if (cached) return cached

    const rankedStr = String(Boolean(isRanked))
    const matches = []
    for (const cacheKey in cache) {
      const separator = cacheKey.lastIndexOf('_')
      const v = cacheKey.slice(0, separator)
      const ranked = cacheKey.slice(separator + 1)
      if (ranked !== rankedStr) continue

      const included = exclusiveVersion ? utils.isVersionEqual(v, version) : utils.isVersionGreaterOrEqual(v, version)
      if (included) {
        const bucket = cache[cacheKey]
        for (let i = 0; i < bucket.length; i++) matches.push(bucket[i])
      }
    }

    const result = markRaw(matches)
    // Only memoize once the mode has published; before that the answer is
    // legitimately empty and must not stick.
    if (state[key].loaded) memo.set(queryKey, result)
    return result
  }

  const getLatestVersion = () => getVersionOptions()[0] || MINIMUM_VERSION

  /**
   * Sorted (newest first) deduplicated list of version buckets in the cache,
   * across both modes, for dropdowns and trend charts.
   */
  const getVersionOptions = () => {
    if (versionOptionsCache) return versionOptionsCache

    const seen = new Set()
    for (const cacheKey in cache) {
      seen.add(utils.getVersionBucket(cacheKey.slice(0, cacheKey.lastIndexOf('_'))))
    }
    // Sort descending (newest first) using the same comparison logic
    const options = [...seen].sort((a, b) => {
      if (utils.isVersionGreaterOrEqual(a, b) && utils.isVersionGreaterOrEqual(b, a)) return 0
      return utils.isVersionGreaterOrEqual(a, b) ? -1 : 1
    })

    versionOptionsCache = markRaw(options)
    return versionOptionsCache
  }

  /**
   * Games per day for one mode over roughly the last month, as a compact
   * `[{ date: 'yyyy-mm-dd', count }]` sorted ascending. Semantics match the
   * previous chart code: the cutoff is `now` minus one calendar month in local
   * time, labels are UTC dates, matches without a valid timestamp are skipped.
   * The chart keeps applying its own `slice(1)`.
   */
  const getDailyCounts = (isRanked = true, nowDate = new Date()) => {
    const key = modeKey(isRanked)
    const minDate = new Date(nowDate.getFullYear(), nowDate.getMonth() - 1, nowDate.getDate())
    const minMs = minDate.getTime()

    const cached = dailyCountsCache[key]
    if (cached && cached.minMs === minMs && cached.revision === state[key].revision) return cached.counts

    const matches = getMatches(MINIMUM_VERSION, isRanked, false)
    const countByDay = new Map()
    for (let i = 0; i < matches.length; i++) {
      const playedAtMs = matches[i].playedAtMs
      if (typeof playedAtMs !== 'number' || Number.isNaN(playedAtMs) || playedAtMs < minMs) continue
      const day = Math.floor(playedAtMs / DAY_MS)
      countByDay.set(day, (countByDay.get(day) || 0) + 1)
    }

    const counts = [...countByDay.keys()]
      .sort((a, b) => a - b)
      .map((day) => ({ date: new Date(day * DAY_MS).toISOString().slice(0, 10), count: countByDay.get(day) }))

    const result = markRaw(counts)
    if (state[key].loaded) dailyCountsCache[key] = { minMs, revision: state[key].revision, counts: result }
    return result
  }

  const onChange = (callback) => {
    if (typeof callback === 'function') listeners.push(callback)
  }

  const offChange = (callback) => {
    listeners = listeners.filter((cb) => cb !== callback)
  }

  const getTimings = () => ({ ranked: timings.ranked, normal: timings.normal })

  return {
    state,
    cache,
    fetchTimestamps,
    isFetching,
    latestMatchAt,
    ensureMatches,
    retryMatches,
    fetchAllMatches,
    isLoaded,
    getModeState,
    getMatches,
    getLatestVersion,
    getVersionOptions,
    getDailyCounts,
    onChange,
    offChange,
    emitChange,
    getTimings,
  }
}

const fmt = (ms) => (typeof ms === 'number' ? `${ms.toFixed(0)}ms` : 'n/a')

const MatchesCache = createMatchesCache()

export default MatchesCache
